/**
 * useDynamicTree Hook
 *
 * React hook for managing tree-based file/folder picker navigation.
 * Supports x-ms-dynamic-tree pattern from Azure connectors.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { ConnectionReference } from '@/types/connectionReference';
import type { DynamicTreeConfig } from '@/types/operationParameter';
import type { DereferencedSwagger } from '@/parsers/SwaggerParser';
import {
  fetchTreeItems,
  getTreeDependencyParameterNames,
  areTreeDependenciesSatisfied,
  type TreeItem,
} from '@/services/dynamicTreeService';
import { isManagedApiConnector } from '@/utils/connections';
import { useConnectorSwagger } from '@/hooks/useConnectorSwagger';
import { useMiAudience } from '@/hooks/useMiAudience';
import { useManagedConnectionInfo } from '@/hooks/useManagedConnectionInfo';
import { useAuth } from '@/auth';
import { isConnectionReferenceComplete } from '@/services/connectionService';
import { useDraftModeStore, selectIsDraftMode } from '@/stores/draftModeStore';

// =============================================================================
// TYPES
// =============================================================================

export interface UseDynamicTreeOptions {
  /** Dynamic tree configuration from the parameter */
  treeConfig: DynamicTreeConfig | undefined;
  /** Connection reference for API calls */
  connectionReference: ConnectionReference | undefined;
  /** Connector ID */
  connectorId: string;
  /** Current values of all parameters (for resolving dependencies) */
  parameterValues: Record<string, unknown>;
  /**
   * Connector swagger spec. Required for managed API connectors so the
   * picker can resolve the operation path/method when calling
   * `/dynamicInvoke`. If omitted, the hook auto-fetches via
   * `useConnectorSwagger` (mirrors `useConnectorDynamicList`).
   */
  connectorSwagger?: DereferencedSwagger | null | undefined;
  /** Whether to auto-fetch root items when dependencies are satisfied */
  autoFetch?: boolean;
}

export interface UseDynamicTreeResult {
  /** Current visible items in the tree */
  items: TreeItem[];
  /** Breadcrumb path (folder hierarchy) */
  breadcrumb: BreadcrumbItem[];
  /** Currently selected item */
  selectedItem: TreeItem | null;
  /** Whether items are currently being fetched */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Whether all required dependencies are satisfied */
  dependenciesSatisfied: boolean;
  /** List of missing dependency parameter names */
  missingDependencies: string[];
  /**
   * Connection reference is missing or a placeholder shell (template-applied
   * without a real connection). When true the hook suppresses fetches to
   * avoid 400s / MisMatchingOAuthClaims and the picker should prompt the
   * user to configure a connection first.
   */
  missingConnection: boolean;
  /** Open a folder (navigate into it) */
  openFolder: (item: TreeItem) => void;
  /** Navigate to a breadcrumb position */
  navigateTo: (index: number) => void;
  /** Select an item (file or folder depending on settings) */
  selectItem: (item: TreeItem) => void;
  /** Clear selection */
  clearSelection: () => void;
  /** Refresh current folder contents */
  refresh: () => void;
  /** Go back to root */
  goToRoot: () => void;
}

export interface BreadcrumbItem {
  /** Display name */
  label: string;
  /** Tree item data (null for root) */
  item: TreeItem | null;
}

// =============================================================================
// HOOK IMPLEMENTATION
// =============================================================================

/**
 * Hook for managing tree-based file/folder navigation.
 *
 * Usage:
 * ```tsx
 * const {
 *   items,
 *   breadcrumb,
 *   selectedItem,
 *   isLoading,
 *   openFolder,
 *   navigateTo,
 *   selectItem,
 * } = useDynamicTree({
 *   treeConfig: parameter.info.dynamicTree,
 *   connectionReference,
 *   connectorId,
 *   parameterValues,
 * });
 * ```
 */
export function useDynamicTree({
  treeConfig,
  connectionReference,
  connectorId,
  parameterValues,
  connectorSwagger: providedSwagger,
  autoFetch = true,
}: UseDynamicTreeOptions): UseDynamicTreeResult {
  // State
  const [items, setItems] = useState<TreeItem[]>([]);
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([{ label: 'Root', item: null }]);
  const [selectedItem, setSelectedItem] = useState<TreeItem | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { getOttoToken } = useAuth();

  // Draft vs published designer mode. Part of the fetch key so a mode switch
  // re-resolves app-setting references against the correct env-settings.
  const isDraftMode = useDraftModeStore(selectIsDraftMode);

  const isManagedApi = useMemo(() => isManagedApiConnector(connectorId), [connectorId]);

  // Auto-fetch swagger for managed API connectors when caller didn't provide
  // one. Service-provider connectors don't need swagger — the runtime
  // resolves operations by operationId via the operationGroups path.
  const shouldAutoFetchSwagger = !providedSwagger && isManagedApi;
  const { data: fetchedSwaggerResult, isLoading: isSwaggerLoading } = useConnectorSwagger(
    shouldAutoFetchSwagger ? connectorId : null
  );
  const connectorSwagger = useMemo(
    () => providedSwagger ?? fetchedSwaggerResult?.swagger ?? null,
    [providedSwagger, fetchedSwaggerResult]
  );

  // Returns undefined for non-MI connections; the service branches on auth type.
  const miAudience = useMiAudience(connectorId);
  // Mirrors LogicAppsUX `await getConnection(...)` — `isReady` gates fetch so
  // MI connections don't hit the bare route before classification settles.
  const { isManagedIdentity, connectionRuntimeUrl, isReady } = useManagedConnectionInfo(
    connectorId,
    connectionReference?.connection.id ?? null
  );

  // Reference must be present *and* fully configured. A placeholder shell
  // (template-applied without picking a real connection) would trigger
  // /dynamicInvoke 400s, so we suppress fetches and surface to the UI.
  const missingConnection = useMemo(
    () => !connectionReference || !isConnectionReferenceComplete(connectionReference),
    [connectionReference]
  );

  // Refs
  const currentFolderRef = useRef<TreeItem | null>(null);
  const lastFetchKeyRef = useRef<string | null>(null);
  // Tracks whether we've kicked off the initial root fetch for the current
  // picker-open session. Without this guard the auto-fetch effect can
  // re-fire mid-browse, abort the in-flight folder fetch, and reload root
  // on top — producing "breadcrumb advances but items still show root".
  // Reset when `autoFetch` flips back to false (popover close).
  const hasAutoFetchedRef = useRef(false);
  const isMountedRef = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  // Get dependency info
  const dependencyNames = useMemo(() => {
    if (!treeConfig) return [];
    return getTreeDependencyParameterNames(treeConfig);
  }, [treeConfig]);

  // Check if dependencies are satisfied
  const { dependenciesSatisfied, missingDependencies } = useMemo(() => {
    if (!treeConfig) {
      return { dependenciesSatisfied: false, missingDependencies: [] as string[] };
    }

    const satisfied = areTreeDependenciesSatisfied(treeConfig, parameterValues);
    const missing = dependencyNames.filter((name) => {
      const value = parameterValues[name];
      return value === undefined || value === null || value === '';
    });

    return {
      dependenciesSatisfied: satisfied,
      missingDependencies: missing,
    };
  }, [treeConfig, parameterValues, dependencyNames]);

  // Fetch items for a given folder. Both managed-API and service-provider
  // tree pickers route through `/dynamicInvoke` on the Otto data plane, so
  // an Otto-audience token is always required (ARM tokens here produce
  // `MisMatchingOAuthClaims`).
  const fetchItems = useCallback(async (folder: TreeItem | null) => {
    if (!treeConfig || !connectionReference || missingConnection) {
      return;
    }
    // `isReady` blocks fire until the connection ARM has settled (LogicAppsUX `await getConnection` parity).
    if (!isReady) {
      return;
    }
    // Managed APIs need swagger to resolve the operation path; wait until
    // it loads.
    if (isManagedApi && !connectorSwagger) {
      return;
    }

    const accessToken = await getOttoToken();
    if (!accessToken) {
      setError('Unable to acquire access token');
      return;
    }

    const pickerInfo = folder ? treeConfig.browse : treeConfig.open;

    const fetchKey = JSON.stringify({
      operationId: pickerInfo.operationId,
      connectionId: connectionReference.connection.id,
      folder: folder?.value ?? null,
      params: parameterValues,
      // MI fields in the key so MI connections re-fetch once detection settles.
      miAudience: miAudience ?? '',
      isManagedIdentity,
      connectionRuntimeUrl: connectionRuntimeUrl ?? '',
      isDraftMode,
    });
    if (fetchKey === lastFetchKeyRef.current && !error) {
      return;
    }

    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    lastFetchKeyRef.current = fetchKey;
    currentFolderRef.current = folder;
    setIsLoading(true);
    setError(null);

    try {
      const request: Parameters<typeof fetchTreeItems>[0] = {
        connectionReference,
        connectorId,
        pickerInfo,
        parameterValues,
        accessToken,
        isDraftMode,
        ...(treeConfig.settings ? { settings: treeConfig.settings } : {}),
        ...(folder ? { selectedItem: folder } : {}),
        ...(connectorSwagger ? { connectorSwagger } : {}),
        ...(miAudience ? { miAudience } : {}),
        isManagedIdentity,
        ...(connectionRuntimeUrl ? { connectionRuntimeUrl } : {}),
      };

      const result = await fetchTreeItems(request);

      if (abortController.signal.aborted || !isMountedRef.current) {
        return;
      }

      if (result.error) {
        setError(result.error);
        setItems([]);
      } else {
        setItems(result.items);
        setError(null);
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      if (!isMountedRef.current) {
        return;
      }
      const message = err instanceof Error ? err.message : 'Failed to fetch items';
      setError(message);
      setItems([]);
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [connectorId, getOttoToken, treeConfig, connectionReference, parameterValues, error, isManagedApi, connectorSwagger, miAudience, isManagedIdentity, connectionRuntimeUrl, isReady, missingConnection, isDraftMode]);

  // Auto-fetch root once per picker-open session (see hasAutoFetchedRef).
  useEffect(() => {
    if (!autoFetch) {
      hasAutoFetchedRef.current = false;
      return;
    }
    if (hasAutoFetchedRef.current) {
      return;
    }
    if (!dependenciesSatisfied || missingConnection || (isManagedApi && !connectorSwagger)) {
      return;
    }

    hasAutoFetchedRef.current = true;
    fetchItems(null);
  }, [autoFetch, dependenciesSatisfied, missingConnection, isManagedApi, connectorSwagger, fetchItems]);

  // Clear items when dependencies become unsatisfied or the connection
  // disappears / becomes a placeholder shell.
  useEffect(() => {
    if ((!dependenciesSatisfied || missingConnection) && items.length > 0) {
      setItems([]);
      setBreadcrumb([{ label: 'Root', item: null }]);
      setSelectedItem(null);
      currentFolderRef.current = null;
      lastFetchKeyRef.current = null;
    }
  }, [dependenciesSatisfied, missingConnection, items.length]);

  // Open folder (navigate into it)
  const openFolder = useCallback((item: TreeItem) => {
    if (!item.isFolder) {
      return;
    }

    setBreadcrumb((prev) => [...prev, { label: item.displayName, item }]);
    fetchItems(item);
  }, [fetchItems]);

  // Navigate to a breadcrumb position
  const navigateTo = useCallback((index: number) => {
    setBreadcrumb((prev) => {
      if (index < 0 || index >= prev.length) {
        return prev;
      }

      const newBreadcrumb = prev.slice(0, index + 1);
      const targetItem = newBreadcrumb[index]?.item ?? null;
      fetchItems(targetItem);
      return newBreadcrumb;
    });
  }, [fetchItems]);

  // Go to root
  const goToRoot = useCallback(() => {
    setBreadcrumb([{ label: 'Root', item: null }]);
    fetchItems(null);
  }, [fetchItems]);

  // Select an item (only used by external consumers of this hook;
  // FilePickerField commits on click and doesn't read selectedItem).
  const selectItem = useCallback((item: TreeItem) => {
    if (!item.isSelectable) {
      return;
    }
    setSelectedItem(item);
  }, []);

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedItem(null);
  }, []);

  // Refresh current folder
  const refresh = useCallback(() => {
    lastFetchKeyRef.current = null;
    fetchItems(currentFolderRef.current);
  }, [fetchItems]);

  return {
    items,
    breadcrumb,
    selectedItem,
    // Surface swagger loading so callers can show a spinner while it loads.
    isLoading: isLoading || (shouldAutoFetchSwagger && isSwaggerLoading),
    error,
    dependenciesSatisfied,
    missingDependencies,
    missingConnection,
    openFolder,
    navigateTo,
    selectItem,
    clearSelection,
    refresh,
    goToRoot,
  };
}

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Hook to check if a parameter has dynamic tree configuration.
 */
export function useHasDynamicTree(dynamicTree: DynamicTreeConfig | undefined): boolean {
  return useMemo(() => dynamicTree !== undefined, [dynamicTree]);
}

// Re-export TreeItem type for convenience
export type { TreeItem };
